import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Gauge, TrendingUp, TrendingDown, Minus, Loader2, RefreshCw, Sparkles,
  ChevronDown, ChevronUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;
const LS_KEY = "driver_perf_widget_opt_in_v1";

const WINDOW_OPTS = [
  { label: "24h", hours: 24 },
  { label: "7d", hours: 168 },
  { label: "30d", hours: 720 },
];

function getToken() { return localStorage.getItem("driver_token"); }

function fmtMi(n) {
  if (n == null) return "—";
  return `${Math.round(n).toLocaleString()} mi`;
}

function fmtPct(p) {
  if (p == null) return "—";
  return `${Math.round(p)}%`;
}

function trendBadge(delta, unit = "", inverse = false) {
  if (delta == null || Math.abs(delta) < 0.5) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[11px] text-slate-400">
        <Minus className="w-3 h-3" /> flat
      </span>
    );
  }
  const positive = inverse ? delta < 0 : delta > 0;
  const Icon = delta > 0 ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${
      positive ? "text-emerald-600" : "text-amber-600"
    }`}>
      <Icon className="w-3 h-3" />
      {delta > 0 ? "+" : ""}{Math.abs(delta) < 10 ? delta.toFixed(1) : Math.round(delta)}{unit}
    </span>
  );
}

function effTone(pct) {
  if (pct == null) return { ring: "text-slate-300", label: "Awaiting data" };
  if (pct >= 70) return { ring: "text-emerald-500", label: "Great work" };
  if (pct >= 50) return { ring: "text-amber-500", label: "Good — room to grow" };
  return { ring: "text-violet-500", label: "Lots of headroom" };
}

/**
 * Driver Performance widget — private, opt-in, read-only.
 *
 * Backend: GET /api/driver/performance?since_hours=N
 *
 * Surfaces only the authenticated driver's own data. No leaderboard, no
 * comparison to other drivers. Designed for positive reinforcement with a
 * simple trend indicator vs the same-length window prior.
 *
 * Opt-in stored in localStorage `driver_perf_widget_opt_in_v1` so drivers can
 * dismiss it.
 */
export default function PerformanceWidget() {
  const [optedIn, setOptedIn] = useState(() => {
    try { return localStorage.getItem(LS_KEY) !== "false"; } catch { return true; }
  });
  const [windowHours, setWindowHours] = useState(168);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API}/api/driver/performance?since_hours=${windowHours}`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
          validateStatus: () => true,
        }
      );
      if (res.status < 200 || res.status >= 300) {
        setError(`HTTP ${res.status}`);
        return;
      }
      setData(res.data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [windowHours]);

  useEffect(() => {
    if (optedIn) fetchData();
  }, [optedIn, fetchData]);

  const toggleOptIn = (next) => {
    setOptedIn(next);
    try { localStorage.setItem(LS_KEY, next ? "true" : "false"); } catch { /* ignore */ }
  };

  if (!optedIn) {
    return (
      <div
        className="bg-gradient-to-br from-cyan-50 to-emerald-50 border border-cyan-100 rounded-xl p-3 flex items-center justify-between gap-3"
        data-testid="performance-widget-opt-in-prompt"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-4 h-4 text-cyan-600 flex-shrink-0" />
          <p className="text-xs text-slate-700 truncate">
            See your private weekly performance snapshot
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => toggleOptIn(true)}
          className="h-7 text-[11px] px-2"
          data-testid="performance-widget-enable-btn"
        >
          Show
        </Button>
      </div>
    );
  }

  const cur = data?.current || {};
  const trend = data?.trend || {};
  const tone = effTone(cur.efficiency_pct);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="performance-widget">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Gauge className="w-4 h-4 text-cyan-600 flex-shrink-0" />
          <p className="text-sm font-semibold text-slate-800">Your performance</p>
          <span className="text-[10px] text-slate-400 hidden sm:inline">· private to you</span>
        </div>
        <div className="flex items-center gap-1">
          {WINDOW_OPTS.map(o => (
            <button
              key={o.hours}
              onClick={() => setWindowHours(o.hours)}
              className={`text-[11px] px-2 py-0.5 rounded-md ${
                windowHours === o.hours
                  ? "bg-cyan-100 text-cyan-700 font-semibold"
                  : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid={`performance-window-${o.label}`}
            >
              {o.label}
            </button>
          ))}
          <button
            onClick={fetchData}
            className="ml-1 p-1 text-slate-400 hover:text-slate-700"
            data-testid="performance-refresh"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="text-[11px] text-red-600 bg-red-50 rounded p-1.5" data-testid="performance-error">{error}</div>
      )}

      {!data && loading && (
        <div className="py-4 text-center text-slate-400" data-testid="performance-loading">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        </div>
      )}

      {data && (
        <>
          {/* Hero — efficiency ring + tone label */}
          <div className="flex items-center gap-4">
            <div className="relative flex-shrink-0">
              <svg width="72" height="72" viewBox="0 0 72 72" className="-rotate-90">
                <circle cx="36" cy="36" r="30" stroke="#e2e8f0" strokeWidth="6" fill="none" />
                <circle
                  cx="36" cy="36" r="30"
                  stroke="currentColor" strokeWidth="6" fill="none"
                  strokeDasharray={`${(cur.efficiency_pct ?? 0) / 100 * 188.5} 188.5`}
                  strokeLinecap="round"
                  className={tone.ring}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center" data-testid="performance-efficiency-ring">
                <span className="text-lg font-bold text-slate-800">{fmtPct(cur.efficiency_pct)}</span>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Efficiency</p>
              <p className="text-sm font-semibold text-slate-800">{tone.label}</p>
              <div className="mt-0.5">{trendBadge(trend.efficiency_delta_pct, "%")}</div>
            </div>
          </div>

          {/* Stat row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-2.5" data-testid="performance-stat-jobs">
              <p className="text-[10px] uppercase tracking-wide text-emerald-700">Jobs done</p>
              <div className="flex items-baseline justify-between">
                <p className="text-lg font-bold text-emerald-900">{cur.jobs_completed}</p>
                <span>{trendBadge(trend.jobs_delta)}</span>
              </div>
              <p className="text-[10px] text-emerald-700 mt-0.5">{cur.jobs_accepted} accepted</p>
            </div>
            <div className="bg-amber-50 border border-amber-100 rounded-lg p-2.5" data-testid="performance-stat-paid">
              <p className="text-[10px] uppercase tracking-wide text-amber-700">Paid miles</p>
              <div className="flex items-baseline justify-between">
                <p className="text-lg font-bold text-amber-900">{fmtMi(cur.paid_miles)}</p>
                <span>{trendBadge(trend.paid_miles_delta, "")}</span>
              </div>
              <p className="text-[10px] text-amber-700 mt-0.5">earning miles</p>
            </div>
            <div className="bg-violet-50 border border-violet-100 rounded-lg p-2.5" data-testid="performance-stat-deadhead">
              <p className="text-[10px] uppercase tracking-wide text-violet-700">Deadhead</p>
              <p className="text-lg font-bold text-violet-900">{fmtMi(cur.deadhead_miles)}</p>
              <p className="text-[10px] text-violet-700 mt-0.5">empty miles</p>
            </div>
            <div className="bg-slate-50 border border-slate-100 rounded-lg p-2.5" data-testid="performance-stat-total">
              <p className="text-[10px] uppercase tracking-wide text-slate-600">Total miles</p>
              <div className="flex items-baseline justify-between">
                <p className="text-lg font-bold text-slate-800">{fmtMi(cur.miles)}</p>
                <span>{trendBadge(trend.miles_delta)}</span>
              </div>
              <p className="text-[10px] text-slate-500 mt-0.5">all driving</p>
            </div>
          </div>

          {/* Paid vs deadhead bar */}
          {cur.miles > 0 && (
            <div data-testid="performance-bar">
              <div className="flex h-2 rounded-full overflow-hidden border border-slate-200">
                <div className="bg-amber-400" style={{ width: `${(cur.paid_miles / cur.miles) * 100}%` }} />
                <div className="bg-violet-400" style={{ width: `${(cur.deadhead_miles / cur.miles) * 100}%` }} />
              </div>
              <div className="flex justify-between text-[10px] text-slate-500 mt-1">
                <span><span className="inline-block w-2 h-2 rounded-full bg-amber-400 mr-1" />paid {fmtMi(cur.paid_miles)}</span>
                <span><span className="inline-block w-2 h-2 rounded-full bg-violet-400 mr-1" />deadhead {fmtMi(cur.deadhead_miles)}</span>
              </div>
            </div>
          )}

          {/* Self-improvement tip */}
          {cur.miles > 0 && cur.efficiency_pct != null && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-[11px] text-slate-600 leading-snug" data-testid="performance-tip">
              {cur.efficiency_pct >= 70 ? (
                <>Strong utilisation. To squeeze more out, accept return loads on the way home — they're surfaced just above.</>
              ) : cur.efficiency_pct >= 50 ? (
                <>Solid week. Cutting empty miles by even 10% boosts your effective hourly rate noticeably.</>
              ) : (
                <>Your empty-mile share is high. Consider switching status to <span className="font-semibold">Returning Empty</span> earlier to get back-haul matches.</>
              )}
            </div>
          )}

          {/* Expand toggle to hide stats again */}
          <div className="flex items-center justify-between pt-1 border-t border-slate-100">
            <button
              onClick={() => setExpanded(v => !v)}
              className="text-[11px] text-slate-500 hover:text-slate-700 flex items-center gap-1"
              data-testid="performance-toggle-details"
            >
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {expanded ? "Hide" : "Show"} comparison vs prior window
            </button>
            <button
              onClick={() => toggleOptIn(false)}
              className="text-[11px] text-slate-400 hover:text-slate-700"
              data-testid="performance-widget-dismiss-btn"
            >
              Hide widget
            </button>
          </div>

          {expanded && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-[11px]" data-testid="performance-comparison">
              <p className="text-slate-500 mb-1">vs previous {windowHours >= 168 ? `${windowHours / 24}d` : `${windowHours}h`}:</p>
              <div className="grid grid-cols-2 gap-1.5 text-slate-700">
                <div>Total miles: <span className="font-mono">{fmtMi(data.previous?.miles)}</span></div>
                <div>Paid miles: <span className="font-mono">{fmtMi(data.previous?.paid_miles)}</span></div>
                <div>Deadhead: <span className="font-mono">{fmtMi(data.previous?.deadhead_miles)}</span></div>
                <div>Jobs done: <span className="font-mono">{data.previous?.jobs_completed ?? 0}</span></div>
                <div>Efficiency: <span className="font-mono">{fmtPct(data.previous?.efficiency_pct)}</span></div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
